import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatEuro, formatDateFr } from "@/lib/format";
import {
  CreditCard, AlertTriangle, Coins, TrendingDown, Landmark, ArrowLeft,
  PlayCircle, Plus, Pencil, Copy, Archive, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import CreditFormDialog from "./CreditFormDialog";
import SmartLoader from "@/components/common/SmartLoader";

const STATUS_LABELS = { active: "Actif", closed: "Soldé", refinanced: "Refinancé", archived: "Archivé", pending: "En cours" };
const LOAN_LABELS = { amortissable: "Amortissable", in_fine: "In fine", relais: "Relais", pret_travaux: "Prêt travaux", revolving: "Revolving", pret_aide: "Prêt aidé", autre: "Autre" };

/* --- List + dashboard --- */
export function CreditsListPage() {
  const [credits, setCredits] = useState([]);
  const [dash, setDash] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get("/credits").then(({ data }) => setCredits(data.items || []));
    api.get("/credits/dashboard").then(({ data }) => setDash(data));
  };
  useEffect(() => { load(); }, []);

  const handleSaved = (result) => {
    load();
    if (result?.id) navigate(`/app/credits/${result.id}`);
  };

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Financements"
        title="Crédits"
        description="Prêts immobiliers, travaux, garanties, tableau d'amortissement et simulations."
        actions={
          <Button onClick={() => setShowForm(true)} data-testid="credits-add-btn" className="btn-pill">
            <Plus className="mr-2 h-4 w-4" /> Ajouter un crédit
          </Button>
        }
      />
      {dash && (
        <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 mb-8">
          <KpiCard name="credit-remaining" label="Capital restant dû" icon={Landmark} tone="accent" value={formatEuro(dash.totals.remaining_principal)} />
          <KpiCard name="credit-monthly" label="Mensualités totales" icon={Coins} value={formatEuro(dash.totals.monthly_payment_total)} />
          <KpiCard name="credit-interest" label="Intérêts 12 prochains mois" icon={TrendingDown} value={formatEuro(dash.totals.yearly_interest)} />
          <KpiCard name="credit-insurance" label="Assurance annuelle" icon={CreditCard} value={formatEuro(dash.totals.yearly_insurance)} />
        </section>
      )}
      {dash?.alerts?.length > 0 && (
        <div className="card-elevated p-5 mb-8 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2 text-amber-800 font-heading mb-2"><AlertTriangle className="h-4 w-4" /> Alertes</div>
          <ul className="text-sm text-amber-900 space-y-1">
            {dash.alerts.map((a) => <li key={`${a.credit_id}-${a.kind}`}>• {a.label} — {a.message}</li>)}
          </ul>
        </div>
      )}
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-4">Crédit</th><th className="px-6 py-4">Banque</th>
              <th className="px-6 py-4 text-right">Montant</th><th className="px-6 py-4 text-right">Mensualité</th>
              <th className="px-6 py-4">Statut</th><th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {credits.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                  <CreditCard className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" />
                  <div className="mb-3">Aucun crédit enregistré.</div>
                  <Button size="sm" onClick={() => setShowForm(true)} data-testid="credits-empty-add-btn">
                    <Plus className="mr-2 h-4 w-4" /> Ajouter votre premier crédit
                  </Button>
                </td>
              </tr>
            )}
            {credits.map((c) => (
              <tr key={c.id} data-testid={`credit-row-${c.id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3">
                  <div className="font-medium">{c.label}</div>
                  <div className="text-xs text-muted-foreground">{LOAN_LABELS[c.loan_type]} · {c.reference || "—"}</div>
                </td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{c.bank || "—"}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(c.amount)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(c.monthly_payment_total, true)}</td>
                <td className="px-6 py-3"><StatusBadge tone={c.status === "active" ? "success" : "neutral"}>{STATUS_LABELS[c.status]}</StatusBadge></td>
                <td className="px-6 py-3 text-right"><Button asChild variant="ghost" size="sm"><Link to={`/app/credits/${c.id}`}>Ouvrir</Link></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreditFormDialog open={showForm} onOpenChange={setShowForm} onSaved={handleSaved} />
    </div>
  );
}

/* --- Detail --- */
export function CreditDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [credit, setCredit] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [sim, setSim] = useState(null);
  const [simParams, setSimParams] = useState({ kind: "early_repayment", extra_capital: "", new_nominal_rate: "", new_insurance_rate: "", new_duration_months: "" });
  const [showEdit, setShowEdit] = useState(false);
  const [showDup, setShowDup] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmForceDelete, setConfirmForceDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setLoadError(null);
    Promise.all([
      api.get(`/credits/${id}`).then(({ data }) => setCredit(data)),
      api.get(`/credits/${id}/schedule`).then(({ data }) => setSchedule(data)),
    ]).catch((e) => setLoadError(e.response?.data?.detail || "Impossible de charger ce crédit."));
  };
  useEffect(() => { load(); }, [id]);

  const runSimulation = async () => {
    try {
      const payload = { kind: simParams.kind };
      ["extra_capital", "new_nominal_rate", "new_insurance_rate"].forEach((k) => { if (simParams[k]) payload[k] = simParams[k]; });
      if (simParams.new_duration_months) payload.new_duration_months = parseInt(simParams.new_duration_months);
      const { data } = await api.post(`/credits/${id}/simulate`, payload);
      setSim(data);
      toast.success("Simulation calculée (non enregistrée)");
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation impossible"); }
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.delete(`/credits/${id}`, { params: { archive: true } });
      toast.success("Crédit archivé");
      setConfirmArchive(false);
      setConfirmForceDelete(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archivage impossible");
    } finally { setBusy(false); }
  };

  const doDelete = async (force = false) => {
    setBusy(true);
    try {
      await api.delete(`/credits/${id}`, { params: force ? { force: true } : {} });
      toast.success("Crédit supprimé");
      setConfirmDelete(false);
      setConfirmForceDelete(false);
      navigate("/app/credits");
    } catch (e) {
      if (e.response?.status === 409) {
        // Historique lié : on ouvre une seconde boîte de dialogue propre plutôt qu'un
        // window.confirm() natif — l'utilisateur peut alors choisir explicitement
        // entre archivage ou suppression forcée.
        setConfirmDelete(false);
        setConfirmForceDelete(true);
      } else {
        toast.error(e.response?.data?.detail || "Suppression impossible");
      }
    } finally { setBusy(false); }
  };

  if (!credit || !schedule || loadError) return <SmartLoader label="Chargement du crédit…" onRetry={load} error={loadError} />;
  const stats = schedule.stats;

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <Button asChild variant="ghost" size="sm">
          <Link to="/app/credits"><ArrowLeft className="mr-2 h-4 w-4" /> Retour aux crédits</Link>
        </Button>
        <div className="flex flex-wrap gap-2" data-testid="credit-detail-actions">
          <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="credit-edit-btn">
            <Pencil className="mr-2 h-4 w-4" /> Modifier
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowDup(true)} data-testid="credit-duplicate-btn">
            <Copy className="mr-2 h-4 w-4" /> Dupliquer
          </Button>
          {credit.status !== "archived" && (
            <Button variant="outline" size="sm" onClick={() => setConfirmArchive(true)} data-testid="credit-archive-btn">
              <Archive className="mr-2 h-4 w-4" /> Archiver
            </Button>
          )}
          <Button variant="outline" size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setConfirmDelete(true)} data-testid="credit-delete-btn">
            <Trash2 className="mr-2 h-4 w-4" /> Supprimer
          </Button>
        </div>
      </div>
      <div className="card-elevated p-6 lg:p-8 mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-accent">{LOAN_LABELS[credit.loan_type]} · {credit.rate_type}</div>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">{credit.label}</h1>
        <div className="mt-1 text-sm text-muted-foreground">{credit.bank} · Réf. {credit.reference || "—"}</div>
        <div className="mt-4 flex flex-wrap gap-2">
          <StatusBadge tone={credit.status === "active" ? "success" : "neutral"}>{STATUS_LABELS[credit.status]}</StatusBadge>
          <StatusBadge tone="neutral">Signé le {formatDateFr(credit.signature_date)}</StatusBadge>
          <StatusBadge tone="neutral">Débloqué le {formatDateFr(credit.release_date)}</StatusBadge>
        </div>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MiniKpi label="Capital restant dû" value={formatEuro(stats.remaining_principal)} tone="accent" />
        <MiniKpi label="Mensualité totale" value={formatEuro(credit.monthly_payment_total, true)} />
        <MiniKpi label="Intérêts restants" value={formatEuro(stats.interests_remaining)} />
        <MiniKpi label="Mois restants" value={stats.months_remaining} />
      </section>

      <Tabs defaultValue="identity">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="identity" data-testid="credit-tab-identity">Fiche</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="credit-tab-schedule">Tableau d&apos;amortissement</TabsTrigger>
          <TabsTrigger value="guarantees" data-testid="credit-tab-guarantees">Garanties</TabsTrigger>
          <TabsTrigger value="attachments" data-testid="credit-tab-attachments">Rattachements</TabsTrigger>
          <TabsTrigger value="simulate" data-testid="credit-tab-simulate">Simulations</TabsTrigger>
        </TabsList>

        <TabsContent value="identity">
          <div className="card-elevated p-6 grid gap-4 md:grid-cols-2 text-sm">
            <Info label="Montant emprunté" value={formatEuro(credit.amount)} />
            <Info label="Apport" value={formatEuro(credit.down_payment)} />
            <Info label="Durée initiale" value={`${credit.initial_duration_months} mois`} />
            <Info label="Taux nominal" value={`${credit.nominal_rate} %`} />
            <Info label="TAEG" value={credit.taeg ? `${credit.taeg} %` : "—"} />
            <Info label="Taux assurance" value={credit.insurance_rate ? `${credit.insurance_rate} %` : "—"} />
            <Info label="Mensualité hors assurance" value={formatEuro(credit.monthly_payment_excl_insurance, true)} />
            <Info label="Assurance /mois" value={formatEuro(credit.monthly_insurance, true)} />
            <Info label="Objet" value={credit.purpose || "—"} />
            <Info label="Société emprunteuse" value={credit.borrower_company_id?.slice(0, 8) || "—"} />
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <div className="card-elevated overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">#</th><th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3 text-right">Mensualité</th>
                  <th className="px-4 py-3 text-right">Capital</th>
                  <th className="px-4 py-3 text-right">Intérêts</th>
                  <th className="px-4 py-3 text-right">Assurance</th>
                  <th className="px-4 py-3 text-right">Solde</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border font-mono">
                {schedule.schedule.map((r) => (
                  <tr key={r.n} className={r.n <= schedule.months_paid ? "bg-secondary/20 text-muted-foreground" : ""}>
                    <td className="px-4 py-2">{r.n}</td>
                    <td className="px-4 py-2">{r.date ? formatDateFr(r.date) : "—"}</td>
                    <td className="px-4 py-2 text-right">{formatEuro(r.payment_total, true)}</td>
                    <td className="px-4 py-2 text-right">{formatEuro(r.principal, true)}</td>
                    <td className="px-4 py-2 text-right">{formatEuro(r.interest, true)}</td>
                    <td className="px-4 py-2 text-right">{formatEuro(r.insurance, true)}</td>
                    <td className="px-4 py-2 text-right">{formatEuro(r.balance, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="guarantees">
          <div className="card-elevated p-6">
            {(credit.guarantees || []).length === 0 && <div className="text-muted-foreground text-sm">Aucune garantie renseignée.</div>}
            <ul className="space-y-3">
              {(credit.guarantees || []).map((g, i) => (
                <li key={i} className="flex items-start justify-between rounded-xl border border-border p-4">
                  <div><div className="font-heading capitalize">{g.kind.replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{g.organism || "—"} · {g.date ? formatDateFr(g.date) : "—"}</div>{g.notes && <div className="text-xs italic mt-1">{g.notes}</div>}</div>
                  <div className="font-mono text-sm">{formatEuro(g.cost, true)}</div>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>

        <TabsContent value="attachments">
          <div className="card-elevated p-6 text-sm space-y-2">
            <div>Immeubles rattachés : {(credit.building_ids || []).length}</div>
            <div>Lots rattachés : {(credit.lot_ids || []).length}</div>
            <div>Projet : {credit.project_id || "—"}</div>
            <div>Travaux : {credit.works_scope || "—"}</div>
            <p className="text-xs text-muted-foreground italic mt-4">Visualisation graphique du rattachement — prochainement.</p>
          </div>
        </TabsContent>

        <TabsContent value="simulate">
          <div className="card-elevated p-6">
            <h3 className="font-heading text-lg mb-4">Simulateur</h3>
            <p className="text-sm text-muted-foreground mb-4">Aucune modification n&apos;est enregistrée tant que vous ne validez pas — les résultats sont indicatifs.</p>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Type de simulation</Label>
                <Select value={simParams.kind} onValueChange={(v) => setSimParams({ ...simParams, kind: v })}>
                  <SelectTrigger data-testid="sim-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="early_repayment">Remboursement anticipé</SelectItem>
                    <SelectItem value="rate_change">Baisse du taux</SelectItem>
                    <SelectItem value="insurance_change">Changement d&apos;assurance</SelectItem>
                    <SelectItem value="duration_change">Nouvelle durée</SelectItem>
                    <SelectItem value="new_loan">Nouvel emprunt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Capital supplémentaire (€)</Label><Input type="number" value={simParams.extra_capital} onChange={(e) => setSimParams({ ...simParams, extra_capital: e.target.value })} data-testid="sim-extra" /></div>
              <div><Label>Nouveau taux (%)</Label><Input type="number" step="0.01" value={simParams.new_nominal_rate} onChange={(e) => setSimParams({ ...simParams, new_nominal_rate: e.target.value })} data-testid="sim-rate" /></div>
              <div><Label>Nouveau taux assurance (%)</Label><Input type="number" step="0.01" value={simParams.new_insurance_rate} onChange={(e) => setSimParams({ ...simParams, new_insurance_rate: e.target.value })} /></div>
              <div><Label>Nouvelle durée (mois)</Label><Input type="number" value={simParams.new_duration_months} onChange={(e) => setSimParams({ ...simParams, new_duration_months: e.target.value })} /></div>
              <div className="flex items-end">
                <Button onClick={runSimulation} className="btn-pill w-full" data-testid="sim-run"><PlayCircle className="mr-2 h-4 w-4" /> Simuler</Button>
              </div>
            </div>
            {sim && (
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                <SimBox label="Nouvelle mensualité" value={formatEuro(sim.simulated.monthly_payment_total, true)} />
                <SimBox label="Impact cash-flow /mois" value={formatEuro(sim.monthly_cashflow_impact, true)} tone={sim.monthly_cashflow_impact < 0 ? "success" : "warning"} />
                <SimBox label="Économie sur intérêts" value={sim.savings != null ? formatEuro(sim.savings, true) : "—"} tone="success" />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <CreditFormDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        credit={credit}
        onSaved={() => load()}
      />
      <CreditFormDialog
        open={showDup}
        onOpenChange={setShowDup}
        duplicateFrom={credit}
        onSaved={(r) => { if (r?.id) navigate(`/app/credits/${r.id}`); }}
      />

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent data-testid="credit-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver ce crédit ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le crédit sera conservé pour l&apos;historique comptable mais masqué des vues actives.
              Vous pouvez le désarchiver plus tard depuis les filtres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doArchive} disabled={busy} data-testid="credit-archive-confirm">
              {busy ? "Archivage…" : "Archiver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="credit-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes les traces d&apos;historique liées à ce crédit
              seront également effacées. Préférez l&apos;archivage si vous n&apos;êtes pas sûr.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doDelete(false)}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="credit-delete-confirm"
            >
              {busy ? "Suppression…" : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fallback quand le backend refuse la suppression pour cause d'historique lié.
          L'utilisateur choisit explicitement entre archiver (recommandé) ou forcer la suppression. */}
      <AlertDialog open={confirmForceDelete} onOpenChange={setConfirmForceDelete}>
        <AlertDialogContent data-testid="credit-force-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ce crédit a un historique</AlertDialogTitle>
            <AlertDialogDescription>
              Des paiements, des simulations ou des documents comptables sont rattachés à ce crédit.
              Nous vous recommandons de l&apos;archiver — il restera consultable dans votre historique,
              mais sera masqué des vues actives.
              <br /><br />
              Si vous souhaitez tout de même le supprimer <strong>définitivement</strong>, ces
              données liées seront également effacées. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <Button
              variant="outline"
              disabled={busy}
              onClick={doArchive}
              data-testid="credit-force-delete-archive"
            >
              <Archive className="mr-2 h-4 w-4" />
              {busy ? "Archivage…" : "Archiver plutôt"}
            </Button>
            <AlertDialogAction
              onClick={() => doDelete(true)}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="credit-force-delete-confirm"
            >
              {busy ? "Suppression…" : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MiniKpi({ label, value, tone }) {
  return (
    <div className="card-elevated p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-accent">{label}</div>
      <div className={`mt-2 font-heading text-xl ${tone === "accent" ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="mt-0.5">{value}</div></div>;
}
function SimBox({ label, value, tone }) {
  const bg = tone === "success" ? "bg-emerald-50 text-emerald-800" : tone === "warning" ? "bg-amber-50 text-amber-800" : "bg-secondary text-foreground";
  return <div className={`rounded-xl p-5 ${bg}`}><div className="text-xs font-bold uppercase tracking-widest">{label}</div><div className="mt-1 font-serif text-2xl">{value}</div></div>;
}
